import React, { useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";

export default function MenuButton(props) {
    const {
        icon: Icon,
        activeIcon: ActiveIcon,
        label,
        to,
        onClick,
        onMobileNavigate,
        smallImage = '',
        extraStyle = '',
        isEditMode = false,
        onMoveUp,
        onMoveDown,
        index,
        isFirst = false,
        isLast = false
    } = props;

    const baseStyle =
        "flex flex-row items-center justify-between w-full rounded-lg font-bold p-4 gap-2 group hover:bg-white hover:text-dark-purple transition";
    
    const dragRef = useRef(null);
    const location = useLocation();

    const handleDragStart = (e) => {
        e.dataTransfer.setData("text/plain", index.toString());
        // Add a styling class to the dragged element
        setTimeout(() => {
            if (dragRef.current) {
                dragRef.current.classList.add("opacity-50");
                // Set the drag image (optional)
                try {
                    const rect = dragRef.current.getBoundingClientRect();
                    e.dataTransfer.setDragImage(dragRef.current, rect.width / 2, rect.height / 2);
                } catch (error) {
                    console.error('Error setting drag image:', error);
                }
            }
        }, 0);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (dragRef.current) {
            // Determine if we're in the top or bottom half of the element
            const rect = dragRef.current.getBoundingClientRect();
            const y = e.clientY - rect.top;
            
            // Remove both classes first
            dragRef.current.classList.remove("border-t-4", "border-b-4", "border-yellow");
            
            if (y < rect.height / 2) {
                // Add top border to indicate item will be placed above
                dragRef.current.classList.add("border-t-4", "border-yellow");
            } else {
                // Add bottom border to indicate item will be placed below
                dragRef.current.classList.add("border-b-4", "border-yellow");
            }
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.currentTarget.classList.add("bg-dark-blue/30");
    };

    const handleDragLeave = (e) => {
        e.currentTarget.classList.remove("bg-dark-blue/30");
        if (dragRef.current) {
            dragRef.current.classList.remove("border-t-4", "border-b-4", "border-yellow");
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.currentTarget.classList.remove("bg-dark-blue/30");
        if (dragRef.current) {
            dragRef.current.classList.remove("border-t-4", "border-b-4", "border-yellow");
        }
        
        const draggedIndex = parseInt(e.dataTransfer.getData("text/plain"));
        const rect = dragRef.current.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const dropIndex = y < rect.height / 2 ? index : index + 1;
        
        // Get current menu items from context
        const items = [...props.menuItems];
        const [draggedItem] = items.splice(draggedIndex, 1);
        items.splice(dropIndex > draggedIndex ? dropIndex - 1 : dropIndex, 0, draggedItem);
        
        // Update menu order
        props.onReorder(items);
    };

    const handleDragEnd = () => {
        if (dragRef.current) {
            dragRef.current.classList.remove("opacity-50", "border-t-4", "border-b-4", "border-yellow");
        }
        
        // Clean up any remaining drag effects on all menu items
        document.querySelectorAll('.menu-button').forEach(item => {
            item.classList.remove("bg-dark-blue/30", "border-t-4", "border-b-4", "border-yellow");
        });
    };

    const handleClick = (e) => {
        // Prevent navigation if already on the same route
        const currentPath = location.pathname;
        const targetPath = to.replace('#', '');

        if (currentPath === targetPath) {
            e.preventDefault();
            console.log('Already on route, preventing navigation');
            return;
        }

        // Call mobile navigation handler if provided (closes mobile menu)
        if (onMobileNavigate) {
            onMobileNavigate();
        }

        // Call the original onClick if provided
        if (onClick) {
            onClick(e);
        }
    };

    return (
        <div 
            className="w-full menu-button relative transition-all"
            ref={dragRef}
            draggable={isEditMode}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
        >
            <NavLink
                className={({ isActive }) =>
                    isActive
                        ? `${baseStyle} bg-white text-dark-purple ${extraStyle}`
                        : `${baseStyle} bg-white/30 ${extraStyle}`
                }
                to={to}
                onClick={handleClick}
            >
                {({ isActive }) => (
                    <>
                        {isEditMode && (
                            <div className="flex items-center cursor-grab mr-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </div>
                        )}
                        <div className="flex items-center flex-1">
                            <Icon
                                className={`
                                ${isActive ? 'hidden' : 'block group-hover:hidden'}
                            `}
                            />
                            <ActiveIcon
                                className={`
                                ${isActive ? 'block' : 'hidden group-hover:block'}
                            `}
                            />
                            <span className="block flex-grow text-sm lg:text-xl whitespace-nowrap text-left ml-2">
                                {label}
                            </span>
                        </div>
                    </>
                )}
            </NavLink>
        </div>
    );
}